import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Sparkle, ShieldCheck, Warning, WarningCircle, Info, X, Copy, ArrowsClockwise,
  CheckCircle, Lightbulb,
} from "@phosphor-icons/react";

const AREA_LABEL = {
  titulo: "Título",
  descripcion: "Descripción",
  fotos: "Fotografías",
  info: "Datos clave",
  precio: "Precio",
  calidad: "Calidad general",
  ubicacion: "Ubicación",
};

const LEVEL_META = {
  critical: { icon: WarningCircle, chip: "border-red-500/40 bg-red-500/10 text-red-300",       dot: "bg-red-400",     label: "Crítico" },
  warning:  { icon: Warning,       chip: "border-amber-500/40 bg-amber-500/10 text-amber-300", dot: "bg-amber-400",   label: "Atención" },
  info:     { icon: Info,          chip: "border-[#C8A24A]/40 bg-[#C8A24A]/10 text-[#C8A24A]", dot: "bg-[#C8A24A]",   label: "Sugerencia" },
};

const GRADE_META = {
  A: { ring: "ring-emerald-400/60", text: "text-emerald-300" },
  B: { ring: "ring-[#C8A24A]/60",   text: "text-[#C8A24A]" },
  C: { ring: "ring-amber-400/60",   text: "text-amber-300" },
  D: { ring: "ring-red-400/60",     text: "text-red-300" },
};

/**
 * Publication Auditor — Fase 5.1
 * Educational (never blocks publish). ATLAS gives recommendations; the advisor
 * decides. Uses the API endpoint POST /atlas/audit.
 */
export function PublicationAuditor({ propertyData, onApplyField }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true); setError("");
    try {
      const payload = {
        ...propertyData,
        price: Number(propertyData.price) || 0,
        bedrooms: Number(propertyData.bedrooms) || 0,
        bathrooms: Number(propertyData.bathrooms) || 0,
        area_m2: Number(propertyData.area_m2) || 0,
        latitude: propertyData.latitude === "" ? null : Number(propertyData.latitude) || null,
        longitude: propertyData.longitude === "" ? null : Number(propertyData.longitude) || null,
      };
      const { data } = await api.post("/atlas/audit", payload);
      setAudit(data);
      setExpanded(true);
    } catch (err) {
      setError(formatApiError(err, "ATLAS no pudo auditar la publicación"));
    } finally { setLoading(false); }
  };

  if (!audit && !loading) {
    return (
      <div className="rounded-2xl border border-[#C8A24A]/30 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] p-5" data-testid="auditor-idle">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C8A24A]/10 border border-[#C8A24A]/30 flex items-center justify-center flex-shrink-0">
            <Sparkle size={20} weight="fill" className="text-[#C8A24A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold">ATLAS · Director Comercial</p>
            <h3 className="font-heading text-xl mt-1">¿Auditamos tu publicación?</h3>
            <p className="text-xs text-[#C8A24A]/70 mt-1">
              ATLAS revisará título, descripción, fotos, precio y datos clave. Te dará un
              Publication Score y recomendaciones concretas para mejorar. No es obligatorio y
              tú decides qué aplicar.
            </p>
            <Button
              onClick={run}
              className="mt-4 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-5"
              data-testid="auditor-run-btn"
            >
              <Sparkle size={14} weight="fill" className="mr-1.5" /> Auditar con ATLAS
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#C8A24A]/30 bg-[#1A1A1A] p-5 animate-pulse" data-testid="auditor-loading">
        <div className="flex items-center gap-3">
          <ArrowsClockwise size={20} className="text-[#C8A24A] animate-spin" />
          <p className="text-sm text-[#C8A24A]/70">ATLAS está auditando tu publicación…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5" data-testid="auditor-error">
        <p className="text-sm text-red-300">{error}</p>
        <Button onClick={run} variant="outline" className="mt-3 h-10" data-testid="auditor-retry-btn">Reintentar</Button>
      </div>
    );
  }

  const meta = GRADE_META[audit.grade] || GRADE_META.C;
  const grouped = { critical: [], warning: [], info: [] };
  audit.recommendations.forEach(r => (grouped[r.level] || grouped.info).push(r));

  return (
    <div className="rounded-2xl border border-[#C8A24A]/30 bg-[#1A1A1A] overflow-hidden" data-testid="auditor-result">
      <div className="p-5 sm:p-6 flex items-start gap-4 flex-wrap border-b border-[#C8A24A]/10">
        <div className={`relative w-20 h-20 rounded-full flex items-center justify-center ring-4 ${meta.ring} bg-[#0A0A0A] flex-shrink-0`} data-testid="auditor-score-ring">
          <div className="text-center">
            <p className={`text-3xl font-heading font-bold ${meta.text}`} data-testid="auditor-score">{audit.score}</p>
            <p className={`text-xs uppercase tracking-widest font-semibold ${meta.text} -mt-0.5`}>{audit.grade}</p>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold inline-flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" className="text-[#C8A24A]" /> Publication Score {audit.llm_used && "· ATLAS AI"}
          </p>
          <h3 className="font-heading text-2xl mt-1" data-testid="auditor-level">
            {audit.level === "excelente" && "Publicación premium"}
            {audit.level === "bueno" && "Buena publicación"}
            {audit.level === "mejorable" && "Publicación mejorable"}
            {audit.level === "crítico" && "Necesita mejoras"}
          </h3>
          <p className="text-sm text-[#C8A24A]/70 mt-1">{audit.summary}</p>
          <p className="text-xs text-[#C8A24A]/50 mt-2 inline-flex items-center gap-1.5">
            <ShieldCheck size={11} weight="fill" /> Educativo · Tú decides qué aplicar.
          </p>
        </div>
        <Button variant="outline" onClick={() => setExpanded(v => !v)} className="h-9" data-testid="auditor-toggle-btn">
          {expanded ? "Ocultar" : "Ver detalle"}
        </Button>
      </div>

      {expanded && (
        <div className="p-5 sm:p-6 space-y-4">
          {audit.recommendations.length === 0 ? (
            <div className="text-center py-6" data-testid="auditor-empty">
              <CheckCircle size={32} className="mx-auto text-emerald-400 mb-2" />
              <p className="text-sm text-[#C8A24A]/80">Sin observaciones. Tu publicación está lista.</p>
            </div>
          ) : (
            ["critical", "warning", "info"].map(lvl => {
              if (grouped[lvl].length === 0) return null;
              const m = LEVEL_META[lvl];
              const Icon = m.icon;
              return (
                <section key={lvl} data-testid={`auditor-group-${lvl}`}>
                  <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2 inline-flex items-center gap-1.5">
                    <Icon size={11} weight="fill" /> {m.label} ({grouped[lvl].length})
                  </p>
                  <ul className="space-y-2">
                    {grouped[lvl].map((r, i) => (
                      <li
                        key={i}
                        data-testid={`auditor-rec-${lvl}-${i}`}
                        className={`p-3 rounded-xl border ${m.chip}`}
                      >
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs uppercase tracking-wider font-bold opacity-80">
                              {AREA_LABEL[r.area] || r.area}
                            </p>
                            <p className="text-sm font-semibold mt-0.5">{r.message}</p>
                            <p className="text-xs opacity-80 mt-1 flex items-start gap-1.5">
                              <Lightbulb size={11} weight="fill" className="mt-0.5 flex-shrink-0" />
                              {r.suggestion}
                            </p>
                            {r.auto_fix && (
                              <div className="mt-2 p-2 rounded-lg bg-black/30 border border-current/20">
                                <p className="text-xs uppercase tracking-widest opacity-70 mb-1">Propuesta ATLAS</p>
                                <p className="text-xs whitespace-pre-wrap break-words">{r.auto_fix.value}</p>
                                <div className="mt-2 flex gap-2">
                                  {onApplyField && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onApplyField(r.auto_fix.field, r.auto_fix.value);
                                        toast.success(`ATLAS actualizó ${AREA_LABEL[r.area]?.toLowerCase() || r.auto_fix.field}`);
                                      }}
                                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md bg-[#C8A24A] text-[#111111] hover:bg-[#B88C2D]"
                                      data-testid={`auditor-apply-${r.area}-${i}`}
                                    >
                                      <CheckCircle size={11} weight="fill" /> Aplicar propuesta
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => { navigator.clipboard.writeText(r.auto_fix.value); toast.success("Copiado"); }}
                                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-current/30 hover:bg-current/10"
                                    data-testid={`auditor-copy-${r.area}-${i}`}
                                  >
                                    <Copy size={11} /> Copiar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })
          )}
          <div className="pt-4 border-t border-[#C8A24A]/10 flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={run} className="h-10" data-testid="auditor-rerun-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Re-auditar
            </Button>
            <p className="text-xs text-[#C8A24A]/50 flex-1">
              ATLAS es una recomendación, no una regla. Publica cuando estés convencido.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
